/**
 * Video Story — Web Components
 * ============================================================
 * Performance-first: lazy loading (preload="none"), IntersectionObserver,
 * rAF-based progress bar, no external dependencies.
 *
 * Architecture:
 *  - <video-stories-component>  → thumbnail row controller
 *  - <video-story-modal>        → modal player (appended to <body>)
 *
 * The modal is moved to <body> on connectedCallback to escape any
 * stacking context that would break z-index on product pages.
 * ============================================================
 */

/* ─── video-stories-component ───────────────────────────────── */
class VideoStoriesComponent extends HTMLElement {
  connectedCallback() {
    this._blockId = this.dataset.blockId;
    this._modal = null;

    // Move the modal template to <body> so it sits outside any
    // stacking context created by the product page layout.
    const tpl = document.getElementById(`vs-modal-tpl-${this._blockId}`);
    if (tpl) {
      const modalEl = tpl.content.firstElementChild.cloneNode(true);
      document.body.appendChild(modalEl);
      this._modal = modalEl;
      // Boot the modal custom element after insertion
      // (customElements.upgrade handles it if already defined)
    }

    // Bind thumbnail triggers
    this.querySelectorAll('.vs-trigger').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const idx = parseInt(btn.dataset.storyIndex, 10);
        this._openModal(idx, btn);
      });
    });
  }

  _openModal(startIndex, triggerEl) {
    if (!this._modal) return;
    this._modal.open(startIndex, triggerEl);
  }
}

/* ─── video-story-modal ─────────────────────────────────────── */
class VideoStoryModal extends HTMLElement {
  connectedCallback() {
    // Prevent double-init (e.g. browser upgrade after body append)
    if (this._initialized) return;
    this._initialized = true;

    // ── State ──────────────────────────────────────────────────
    this._currentIndex = 0;
    this._isMuted = true;       // videos start muted (browser policy)
    this._isPaused = false;
    this._rafId = null;
    this._progressStart = null;
    this._videoDuration = 0;
    this._triggerRef = null;

    // ── DOM refs ───────────────────────────────────────────────
    this._backdrop = this.querySelector('.vs-modal__backdrop');
    this._stage    = this.querySelector('.vs-modal__stage');
    this._slides   = Array.from(this.querySelectorAll('.vs-modal__slide'));
    this._videos   = this.querySelectorAll('.vs-modal__video');
    this._progSegs = Array.from(this.querySelectorAll('.vs-modal__progress-seg'));
    this._muteBtn  = this.querySelector('.vs-modal__mute');
    this._closeBtn = this.querySelector('.vs-modal__close');

    // Tap zones
    this._tapPrev = this.querySelector('.vs-modal__tap--prev');
    this._tapNext = this.querySelector('.vs-modal__tap--next');

    // ── Event listeners ────────────────────────────────────────
    this._closeBtn?.addEventListener('click', () => this.close());
    this._backdrop?.addEventListener('click', () => this.close());
    this._muteBtn?.addEventListener('click', () => this._toggleMute());
    this._tapPrev?.addEventListener('click', () => this._navigate(-1));
    this._tapNext?.addEventListener('click', () => this._navigate(1));

    // Keyboard: Escape → close, ArrowLeft/Right → navigate
    this._onKeyDown = (e) => {
      if (!this.dataset.open) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft')  this._navigate(-1);
      if (e.key === 'ArrowRight') this._navigate(1);
    };
    document.addEventListener('keydown', this._onKeyDown);

    // Visibility change → pause/resume
    this._onVisibilityChange = () => {
      if (document.hidden) this._pauseProgress();
      else if (this.dataset.open === 'true') this._resumeProgress();
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this._stopProgress();
  }

  /* ── Public API ─────────────────────────────────────────────── */

  open(startIndex = 0, triggerEl = null) {
    this._triggerRef = triggerEl;
    document.body.classList.add('vs-modal-open');
    this.dataset.open = 'true';
    this._isPaused = false;

    // Reset all segments
    this._progSegs.forEach((seg) => {
      seg.removeAttribute('data-state');
      seg.querySelector('.vs-modal__progress-fill').style.width = '0%';
    });

    this._goTo(startIndex);

    // Trap focus inside modal
    this._stage?.focus?.();
  }

  close() {
    this.dataset.open = 'false';
    delete this.dataset.open;
    document.body.classList.remove('vs-modal-open');

    this._stopProgress();

    // Pause all videos
    this._videos.forEach((v) => {
      v.pause();
      // Don't reset src — keep loaded for re-open
    });

    // Return focus to trigger
    this._triggerRef?.focus?.();

    // Mark current story as viewed
    if (this._triggerRef) {
      const blockId = this.dataset.blockId;
      const idx = this._triggerRef.dataset.storyIndex;
      const key = `vs-viewed-${blockId}-${idx}`;
      sessionStorage.setItem(key, '1');
      this._triggerRef.dataset.viewed = 'true';
    }
  }

  /* ── Navigation ─────────────────────────────────────────────── */

  _goTo(index) {
    const count = this._slides.length;
    if (index < 0) { this.close(); return; }
    if (index >= count) { this.close(); return; }

    // Deactivate all slides
    this._slides.forEach((s) => {
      s.dataset.active = 'false';
      delete s.dataset.active;
    });

    this._currentIndex = index;
    const slide = this._slides[index];
    slide.dataset.active = 'true';

    // Mark past segments as done
    this._progSegs.forEach((seg, i) => {
      if (i < index) {
        seg.dataset.state = 'done';
        seg.querySelector('.vs-modal__progress-fill').style.width = '100%';
      } else if (i === index) {
        seg.dataset.state = 'active';
        seg.querySelector('.vs-modal__progress-fill').style.width = '0%';
      } else {
        delete seg.dataset.state;
        seg.querySelector('.vs-modal__progress-fill').style.width = '0%';
      }
    });

    // Load + play video (lazy)
    this._loadAndPlay(slide);
  }

  _navigate(direction) {
    this._stopProgress();
    this._goTo(this._currentIndex + direction);
  }

  /* ── Video loading (lazy) ───────────────────────────────────── */

  _loadAndPlay(slide) {
    const video = slide.querySelector('.vs-modal__video');
    if (!video) return;

    this._stopProgress();
    this._progressStart = null;
    this._videoDuration = 0;

    const play = () => {
      video.muted = this._isMuted;
      video.play().then(() => {
        this._videoDuration = video.duration || 15;
        this._startProgress();
      }).catch(() => {
        // Autoplay blocked — try muted
        video.muted = true;
        this._isMuted = true;
        this._syncMuteUI();
        video.play().then(() => {
          this._videoDuration = video.duration || 15;
          this._startProgress();
        });
      });
    };

    if (video.readyState >= 3) {
      // Already loaded enough
      play();
    } else {
      // Lazy: set src from data-src if not yet set
      if (!video.src && video.dataset.src) {
        video.src = video.dataset.src;
      }

      const onCanPlay = () => {
        video.removeEventListener('canplay', onCanPlay);
        play();
      };
      video.addEventListener('canplay', onCanPlay);

      // Also listen for metadata to get duration early
      const onMeta = () => {
        video.removeEventListener('loadedmetadata', onMeta);
        this._videoDuration = video.duration || 15;
      };
      video.addEventListener('loadedmetadata', onMeta);

      video.load();
    }

    // When video ends naturally → next
    video.onended = () => {
      this._stopProgress();
      this._navigate(1);
    };
  }

  /* ── Progress bar (rAF) ─────────────────────────────────────── */

  _startProgress() {
    this._stopProgress();
    this._isPaused = false;
    this._progressStart = performance.now();
    this._tickProgress();
  }

  _tickProgress() {
    if (this._isPaused) return;

    const seg = this._progSegs[this._currentIndex];
    if (!seg) return;

    const fill = seg.querySelector('.vs-modal__progress-fill');
    const elapsed = performance.now() - this._progressStart;
    const duration = (this._videoDuration || 15) * 1000; // ms
    const pct = Math.min((elapsed / duration) * 100, 100);

    fill.style.width = pct + '%';

    if (pct < 100) {
      this._rafId = requestAnimationFrame(() => this._tickProgress());
    } else {
      this._navigate(1);
    }
  }

  _stopProgress() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _pauseProgress() {
    this._isPaused = true;
    this._stopProgress();
    const video = this._slides[this._currentIndex]?.querySelector('.vs-modal__video');
    video?.pause();
  }

  _resumeProgress() {
    if (!this._isPaused) return;
    this._isPaused = false;
    const video = this._slides[this._currentIndex]?.querySelector('.vs-modal__video');
    if (video) {
      // Adjust progressStart to account for elapsed time before pause
      const fill = this._progSegs[this._currentIndex]?.querySelector('.vs-modal__progress-fill');
      const currentPct = parseFloat(fill?.style.width || '0') / 100;
      const remaining = (1 - currentPct) * (this._videoDuration || 15) * 1000;
      this._progressStart = performance.now() - (currentPct * (this._videoDuration || 15) * 1000);

      video.play().catch(() => {});
      this._tickProgress();
    }
  }

  /* ── Mute ───────────────────────────────────────────────────── */

  _toggleMute() {
    this._isMuted = !this._isMuted;
    const video = this._slides[this._currentIndex]?.querySelector('.vs-modal__video');
    if (video) video.muted = this._isMuted;
    this._syncMuteUI();
  }

  _syncMuteUI() {
    if (!this._muteBtn) return;
    this._muteBtn.dataset.muted = String(this._isMuted);
    this._muteBtn.setAttribute(
      'aria-label',
      this._isMuted ? 'Ativar som' : 'Silenciar'
    );
  }
}

/* ─── Register custom elements ──────────────────────────────── */
if (!customElements.get('video-stories-component')) {
  customElements.define('video-stories-component', VideoStoriesComponent);
}
if (!customElements.get('video-story-modal')) {
  customElements.define('video-story-modal', VideoStoryModal);
}

/* ─── Restore "viewed" state from sessionStorage ────────────── */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.vs-trigger').forEach((btn) => {
    const blockId = btn.closest('[data-block-id]')?.dataset.blockId;
    const idx = btn.dataset.storyIndex;
    if (blockId && sessionStorage.getItem(`vs-viewed-${blockId}-${idx}`)) {
      btn.dataset.viewed = 'true';
    }
  });
});
